import { Dayjs } from 'dayjs';

import { EboObject } from '@app/models/ebo/base-classes/ebo-object';
import { Employee } from '@app/models/ebo/employee/employee';
import { Tenant } from '@app/models/ebo/tenant/tenant';
import { SDate, isString } from '@summit/core';

import { BackEndClientAction } from './back-end-client-action';
import { BackEndJobResult, BackEndJobResultMapping } from './back-end-job-result.enum';
import { BackEndJobStatus, BackEndJobStatusMapping } from './back-end-job-status.enum';
import { JobWorkRequestType, JobWorkRequestTypeMapping } from './job-work-request-type.enum';

export class BackEndJob extends EboObject {
  tenant: Tenant;
  employee: Employee;
  name: string;
  currentWorkRequestID: string; // does the client need?
  currentWorkRequest: JobWorkRequestType;
  currentWorkTask: number;
  currentStatusText: string;
  startTime: Dayjs;
  lastUpdate: Dayjs;
  endTime: Dayjs;
  attempts: number;
  status: BackEndJobStatus;
  result: BackEndJobResult;
  clientActions: BackEndClientAction[];

  get fedExID(): string {
    return this.employee?.fedExID;
  }

  constructor(jsonData?: Partial<BackEndJob>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.tenant) {
        this.tenant = new Tenant(jsonData.tenant);
      }
      if (jsonData.employee) {
        this.employee = new Employee(jsonData.employee);
      }
      if (jsonData.startTime) {
        this.startTime = SDate.newUtc(jsonData.startTime);
      }
      if (jsonData.lastUpdate) {
        this.lastUpdate = SDate.newUtc(jsonData.lastUpdate);
      }
      if (jsonData.endTime) {
        this.endTime = SDate.newUtc(jsonData.endTime);
      }
      if (jsonData.clientActions) {
        //console.log(`Parsing clientActions: ${jsonData.clientActions}`);
        try {
          this.clientActions = isString(jsonData.clientActions)
            ? JSON.parse(String(jsonData.clientActions)).map((item) => new BackEndClientAction(item))
            : jsonData.clientActions.map((item) => new BackEndClientAction(item));
        } catch (e) {
          console.error(`Error parsing clientActions: ${e}`, jsonData.clientActions);
        }
      }
    }
  }

  public get isInProgress(): boolean {
    return (
      this.status == BackEndJobStatus.Waiting ||
      this.status == BackEndJobStatus.RequestStop ||
      this.status == BackEndJobStatus.Running
    );
  }
  public get isComplete(): boolean {
    return this.status == BackEndJobStatus.Complete;
  }
  public get isStoppable(): boolean {
    return this.status != BackEndJobStatus.RequestStop;
  }
  public get isCancelPending(): boolean {
    return this.status == BackEndJobStatus.RequestStop;
  }
  public get isFailed(): boolean {
    return this.result == BackEndJobResult.Failed;
  }

  getJobIcon(): string {
    let response = 'help';
    switch (this.status) {
      case BackEndJobStatus.Complete:
        switch (this.result) {
          case BackEndJobResult.Successful:
            response = 'check_circle';
            break;
          case BackEndJobResult.Failed:
            response = 'error';
            break;
          default:
            response = 'warning';
            break;
        }
        break;
      case BackEndJobStatus.Waiting:
        response = 'watch_later';
        break;
      case BackEndJobStatus.RequestStop:
        response = 'cancel_schedule_send';
        break;
      default:
        response = 'help';
        break;
    }
    //console.log(`Returning: ${response}`);
    return response;
  }
  getResultIcon(): string {
    if (this.result == BackEndJobResult.Successful) {
      return 'check_circle';
    }
    if (this.result == BackEndJobResult.Failed) {
      return 'error';
    }
    return 'help';
  }
  getWorkRequestName(): string {
    return JobWorkRequestTypeMapping[this.currentWorkRequest ?? 0];
  }
  getWorkTaskName(): string {
    return this.currentWorkTask?.toString();
  }
  getStatusName(): string {
    return BackEndJobStatusMapping[this.status];
  }
  getResultName(): string {
    return BackEndJobResultMapping[this.result];
  }
}
